import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const FAQ = () => {
  const [openItem, setOpenItem] = useState(0);

  const faqItems = [
    {
      question: "What Makes Sonor AI Different From Other AI Solutions?",
      answer: "Sonor AI specializes in AI-powered voice automation for phone calls, not just chatbots or text-based automation. Our voice agents engage in real conversations, handling inbound and outbound calls seamlessly, ensuring businesses never miss an opportunity to connect."
    },
    {
      question: "How Can AI Voice Agents Improve My Business?",
      answer: "Sonor AI automates both inbound and outbound calls to enhance customer engagement and drive revenue. Our AI voice agents can handle lead revival, follow-up calls, appointment scheduling, and customer inquiries—ensuring every opportunity is captured. Whether it's re-engaging cold leads, qualifying prospects, or reducing no-shows with automated reminders, our AI-powered voice solutions work 24/7 to maximize efficiency and conversion rates."
    },
    {
      question: "Can Sonor AI Integrate With My Existing Systems?",
      answer: "Yes! Sonor AI integrates with CRM platforms like HubSpot, GoHighLevel, Salesforce, and scheduling tools like Calendly and Google Calendar. We ensure a seamless transition to AI-powered voice automation without disrupting your current workflows."
    },
    {
      question: "How Does Sonor AI Ensure Natural and Effective Conversations?",
      answer: "We leverage advanced speech recognition, natural language processing (NLP), and adaptive learning to make our AI voice agents sound human-like and context-aware. Our AI can understand intent, ask follow-up questions, and even handle objections."
    },
    {
      question: "Does Sonor AI Offer Custom AI Voice Solutions?",
      answer: "Absolutely! We tailor our AI voice agents to meet the specific needs of your industry—whether it's EdTech, healthcare, real estate, or sales. Our custom solutions ensure that every AI interaction aligns with your business goals and customer expectations."
    }
  ];

  const toggleItem = (index) => {
    setOpenItem(openItem === index ? null : index);
  };

  return (
    <Section id="faq">
      <ContentWrapper>
        <FAQPill>
          Need to Know
        </FAQPill>
        <MainHeading>
          Frequently Asked Questions
        </MainHeading>
      </ContentWrapper>

      <FAQContainer>
        {faqItems.map((item, index) => (
          <FAQItem key={index}>
            <QuestionRow onClick={() => toggleItem(index)} isOpen={openItem === index}>
              <Question>{item.question}</Question>
              <IconContainer isOpen={openItem === index}>
                <ChevronIcon>{openItem === index ? '−' : '+'}</ChevronIcon>
              </IconContainer>
            </QuestionRow>

            <AnimatePresence>
              {openItem === index && (
                <AnswerContainer
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <Answer>{item.answer}</Answer>
                </AnswerContainer>
              )}
            </AnimatePresence>
          </FAQItem>
        ))}
      </FAQContainer>
    </Section>
  );
};

const Section = styled.section`
  padding: 100px 20px;
  max-width: 1200px;
  margin: 0 auto;
`;

const MainHeading = styled.h2`
  font-size: 38px;
  font-weight: 700;
  color: white;
  margin-bottom: 60px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 32px;
  }

  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const FAQContainer = styled.div`
  margin-bottom: 60px;
`;

const FAQItem = styled.div`
  background-color: rgba(17, 17, 17, 0.5);
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 16px;
`;

const QuestionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 30px;
  cursor: pointer;
  transition: color 0.3s ease;
  color: ${props => props.isOpen ? 'white' : 'var(--color-text-gray)'};

  &:hover {
    color: white;
  }
`;

const Question = styled.h3`
  font-size: 22px;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.3s ease;
`;

const ChevronIcon = styled.span`
  font-size: 24px;
  font-weight: 300;
  line-height: 1;
`;

const AnswerContainer = styled(motion.div)`
  overflow: hidden;
`;

const Answer = styled.div`
  color: var(--color-text-gray);
  font-size: 16px;
  line-height: 1.6;
  padding: 0 30px 30px;
`;


const FAQPill = styled.div`
  display: inline-block;
  padding: 8px 20px;
  background-color: rgba(17, 17, 17, 0.9);
  border-radius: 50px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 30px;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;


export default FAQ;
